import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parseNumericInput } from '../utils/numberFormat.js'
import { generateRequestId } from '../utils/requestId.js'
import ConnectionStatus from './ConnectionStatus.jsx'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { handleHotKeyboardShortcut } from '../utils/hotKeyboardShortcuts.js'
import { showError } from '../utils/notifications.js'
import usePasteOverflow from '../hooks/usePasteOverflow.js'
import { t } from '../../i18n.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

registerAllModules()

const DEBOUNCE_DELAY = 800
const NO_INFLATION_POSITION = 0


const parseRateInput = parseNumericInput

function formatRate(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return `${n.toFixed(2)}%`
}

function resizeRates(rates, yearCount) {
  const result = {}
  let lastKnown = 0
  for (let y = 1; y <= yearCount; y++) {
    const key = String(y)
    if (rates[key] !== undefined && rates[key] !== null) {
      lastKnown = Number(rates[key])
    }
    result[key] = lastKnown
  }
  return result
}

export default function GrowthRatesTable({
  valuationId, indexUrl, batchUpdateUrl, batchCreateUrl, createRowUrl, destroyRowUrl,
  csrfToken, readOnly = false, valuationDate, lengthOfAnalysisMonths, translations
}) {
  const hotRef = useRef(null)
  const pendingRef = useRef(new Map())
  const flushTimeoutRef = useRef(null)
  const [rawData, setRawData] = useState([])
  const [loading, setLoading] = useState(true)
  const [connectionStatus, setConnectionStatus] = useState('connecting')
  const [connectionMountEl, setConnectionMountEl] = useState(null)

  const yearCount = useMemo(() => {
    const totalMonths = (lengthOfAnalysisMonths || 0) + 12
    return Math.ceil(totalMonths / 12)
  }, [lengthOfAnalysisMonths])

  // Mount connection badge into card header
  useEffect(() => {
    const container = document.querySelector('[data-react-component-component-value="GrowthRatesTable"]')
    const cardHeader = container?.closest('.card')?.querySelector('.card-header')
    const titleEl = cardHeader?.querySelector('h5.card-title')
    if (!titleEl) return

    const mount = document.createElement('span')
    mount.className = 'tenants-connection-status-mount'
    titleEl.appendChild(mount)
    setConnectionMountEl(mount)

    return () => { setConnectionMountEl(null); mount.remove() }
  }, [])

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await fetch(indexUrl, {
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken }
      })
      const json = await res.json()
      if (json.success && json.data) {
        setRawData(json.data)
      }
    } catch (e) {
      console.error('Failed to fetch growth rates:', e)
    } finally {
      setLoading(false)
    }
  }, [indexUrl, csrfToken])

  useEffect(() => { fetchData() }, [fetchData])

  // EVA-138: paste overflow — create missing rows server-side, then replay.
  // Callbacks reference flushChanges lazily (defined below); they only run
  // after the render completes, so no TDZ issue.
  const { handleBeforePaste, creatingRowsRef } = usePasteOverflow({
    hotRef,
    enabled: !readOnly,
    createRows: async (count) => {
      const res = await fetch(batchCreateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          request_id: generateRequestId(),
          items: Array.from({ length: count }, (_, i) => ({ temp_id: `paste_${Date.now()}_${i}`, data: {} }))
        })
      })
      const json = await res.json()
      return json.success === true
    },
    refetchData: () => fetchData(true), // silent — non-silent fetch unmounts HotTable
    flushNow: () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
      return flushChanges()
    },
    getAnchorId: (row) => rawData[row]?.id,
    resolveAnchorRow: (id) => {
      const idx = rawData.findIndex(r => r.id === id)
      return idx >= 0 ? idx : undefined
    }
  })

  // ActionCable subscription
  useEffect(() => {
    const subscription = cable.subscriptions.create(
      { channel: 'GrowthRatesChannel', valuation_id: valuationId },
      {
        connected() { setConnectionStatus('connected') },
        disconnected() { setConnectionStatus('disconnected') },
        received() {
          // Drop pushes while a paste-create is in flight — a refetch here
          // would race the replay; the post-replay flush resyncs everyone.
          if (creatingRowsRef.current) return
          fetchData(true)
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [valuationId, fetchData, creatingRowsRef])

  // Listen for "Add Row" button click
  useEffect(() => {
    if (readOnly) return
    const handler = async () => {
      try {
        const res = await fetch(createRowUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ title: 'New Rate', request_id: generateRequestId() })
        })
        const json = await res.json()
        if (json.success) {
          await fetchData(true)
          // Focus the title cell of the new row for inline editing
          requestAnimationFrame(() => {
            setTimeout(() => {
              const hot = hotRef.current?.hotInstance
              if (!hot) return
              const lastRow = hot.countRows() - 1
              hot.selectCell(lastRow, 0)
              hot.getActiveEditor()?.beginEditing()
            }, 50)
          })
        }
      } catch (e) {
        console.error('Failed to create row:', e)
      }
    }
    window.addEventListener('addGrowthRateRow', handler)
    return () => window.removeEventListener('addGrowthRateRow', handler)
  }, [readOnly, createRowUrl, csrfToken, fetchData])

  // Build table data from raw records
  const tableData = useMemo(() => {
    return rawData.map((row) => {
      const rates = resizeRates(row.rates || {}, yearCount)
      const cells = [row.title]
      for (let y = 1; y <= yearCount; y++) {
        cells.push(rates[String(y)] ?? 0)
      }
      return cells
    })
  }, [rawData, yearCount])

  // Flush pending changes (pendingRef is keyed by record id, not row index,
  // so a refetch/reorder between edit and flush can't misroute the update)
  const flushChanges = useCallback(async () => {
    const pending = pendingRef.current
    if (pending.size === 0) return

    const items = []
    for (const [recordId, changedCols] of pending.entries()) {
      const changes = {}
      const ratesUpdate = {}
      for (const [col, value] of changedCols.entries()) {
        if (col === 0) {
          changes.title = value
        } else {
          ratesUpdate[String(col)] = value
        }
      }
      if (Object.keys(ratesUpdate).length > 0) {
        changes.rates = ratesUpdate
      }
      items.push({ id: recordId, changes })
    }
    pendingRef.current = new Map()

    if (items.length === 0) return

    try {
      const res = await fetch(batchUpdateUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ items, request_id: generateRequestId() })
      })
      const json = await res.json()
      if (!json.success) {
        // The whole batch rolled back server-side — resync the grid
        showError(t('save_error_invalid', translations))
        fetchData(true)
      }
    } catch (e) {
      console.error('Failed to batch update:', e)
      showError(t('save_error', translations))
      fetchData(true)
    }
  }, [batchUpdateUrl, csrfToken, fetchData, translations])

  // Debounced flush
  const scheduledFlush = useCallback(() => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushTimeoutRef.current = setTimeout(() => flushChanges(), DEBOUNCE_DELAY)
  }, [flushChanges])

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
      flushChanges()
    }
  }, [flushChanges])

  // Delete row handler
  const handleDeleteRow = useCallback(async (rowIndex) => {
    const record = rawData[rowIndex]
    if (!record || record.default_row) return
    if (!confirm(t('delete_confirm', translations, { title: record.title }))) return

    try {
      const res = await fetch(destroyRowUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ id: record.id, request_id: generateRequestId() })
      })
      const json = await res.json()
      if (json.success) {
        fetchData(true)
      }
    } catch (e) {
      console.error('Failed to delete row:', e)
    }
  }, [rawData, destroyRowUrl, csrfToken, fetchData, translations])

  const hotSettings = useMemo(() => ({
    data: tableData,
    rowHeaders: false,
    colHeaders: true,
    fixedColumnsStart: 1,
    themeName: 'ht-theme-horizon',
    licenseKey: HOT_LICENSE_KEY,
    sanitizer: sanitizeHandsontableHtml,
    stretchH: 'all',
    width: '100%',
    height: 'auto',
    manualColumnResize: true,
    contextMenu: readOnly ? false : {
      items: {
        delete_row_custom: {
          name: t('delete_row', translations),
          disabled() {
            const selected = this.getSelectedRangeLast()
            if (!selected) return true
            const row = selected.from.row
            const record = rawData[row]
            return !record || record.default_row
          },
          callback(_key, selection) {
            const row = selection[0]?.start?.row
            if (row !== undefined) handleDeleteRow(row)
          }
        }
      }
    },
    afterGetColHeader(col, th) {
      const holder = th.querySelector('.colHeader')
      if (!holder) return

      if (col === 0) {
        holder.textContent = t('category', translations)
        return
      }

      th.classList.add('htRight')

      if (valuationDate) {
        const d = new Date(valuationDate)
        d.setMonth(d.getMonth() + 12 * col)
        const monthStr = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        holder.innerHTML = `<div style="font-weight:600">${t('y_compact', translations, { n: col })}</div><div style="font-size:0.8em;color:#888">${monthStr}</div>`
      } else {
        holder.textContent = t('year', translations, { n: col })
      }
    },
    cells(row, col) {
      const cellProps = {}

      if (col === 0) {
        const record = rawData[row]
        cellProps.readOnly = readOnly || !record || record.default_row
        return cellProps
      }

      if (readOnly) {
        cellProps.readOnly = true
        return cellProps
      }

      // "No Inflation" row
      const record = rawData[row]
      if (record && record.position === NO_INFLATION_POSITION) {
        cellProps.readOnly = true
      }

      cellProps.renderer = function(instance, td, rowIdx, colIdx, prop, value, cellProperties) {
        td.textContent = formatRate(value)
        td.style.textAlign = 'right'
        if (cellProperties.readOnly) {
          td.style.color = '#999'
        }
      }

      return cellProps
    },
    beforePaste: handleBeforePaste,
    afterChange(changes, source) {
      if (!changes || source === 'loadData' || source === 'server' || readOnly) return

      const hot = hotRef.current?.hotInstance

      setRawData((prev) => {
        const next = prev.map((r) => ({ ...r, rates: { ...r.rates } }))

        changes.forEach(([row, col, oldVal, newVal]) => {
          if (typeof col !== 'number') return
          const record = next[row]
          if (!record) return

          // Title column
          if (col === 0) {
            const trimmed = (newVal || '').trim()
            if (!trimmed) {
              // Revert to old value if empty
              if (hot) hot.setDataAtCell(row, 0, oldVal, 'server')
              return
            }
            record.title = trimmed
            if (!pendingRef.current.has(record.id)) {
              pendingRef.current.set(record.id, new Map())
            }
            pendingRef.current.get(record.id).set(0, trimmed)
            return
          }

          if (record.position === NO_INFLATION_POSITION) return

          const isEmpty = newVal === '' || newVal === null || newVal === undefined
          const parsed = isEmpty ? 0 : (typeof newVal === 'number' ? newVal : parseRateInput(newVal))
          if (parsed === null) return

          // Update this cell and fill-forward
          for (let y = col; y <= yearCount; y++) {
            record.rates[String(y)] = parsed
          }

          // Track pending changes for flush
          if (!pendingRef.current.has(record.id)) {
            pendingRef.current.set(record.id, new Map())
          }
          const colMap = pendingRef.current.get(record.id)
          for (let y = col; y <= yearCount; y++) {
            colMap.set(y, parsed)
          }
        })

        return next
      })

      scheduledFlush()
    },
    afterDocumentKeyDown(event) {
      if (readOnly) return
      const hot = hotRef.current?.hotInstance
      handleHotKeyboardShortcut(event, {
        onAddRow: () => window.dispatchEvent(new CustomEvent('addGrowthRateRow')),
        onDeleteRow: () => {
          if (!hot) return
          const selected = hot.getSelectedRangeLast()
          if (!selected) return
          const row = selected.from.row
          const record = rawData[row]
          if (record && !record.default_row) handleDeleteRow(row)
        }
      })
    }
  }), [tableData, readOnly, rawData, yearCount, valuationDate, handleDeleteRow, scheduledFlush, handleBeforePaste, translations])

  // Expose the Handsontable instance for end-to-end specs and console debugging
  useEffect(() => {
    if (typeof window !== 'undefined' && !loading) {
      window.__evaGrowthRatesHot = hotRef.current?.hotInstance
    }
    return () => {
      if (typeof window !== 'undefined') delete window.__evaGrowthRatesHot
    }
  })

  return (
    <div className="growth-rates-table-container">
      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading', translations)}</span>
          </div>
        </div>
      ) : (
        <HotTable ref={hotRef} {...hotSettings} />
      )}

      <ConnectionStatus
        status={connectionStatus}
        mountEl={connectionMountEl}
        labels={{
          connected: t('connection.live', translations),
          disconnected: t('connection.disconnected', translations),
          connecting: t('connection.connecting', translations),
        }}
      />
    </div>
  )
}
